import React from 'react';
import { Lock } from 'lucide-react';
import {
    FULL_ACCESS_EMAIL,
    FULL_ACCESS_MAILTO,
    TRIAL_SCOPE_SENTENCE,
    accessRequestMailto,
    featureBlurb,
    featureLabel,
    useFeature,
    type FeatureKey,
} from '../auth/features';

/**
 * Shown in place of a page the caller's tier cannot use (deep links). Same
 * wording and Contact us action as the sidebar's LockedFeatureDialog.
 */
export function FeatureUnavailable({ feature }: { feature?: FeatureKey }) {
    const name = feature ? featureLabel(feature) : null;
    const blurb = feature ? featureBlurb(feature) : null;
    return (
        <div className="p-8 flex justify-center" data-testid="feature-unavailable" data-feature={feature}>
            <div className="max-w-md w-full rounded-lg border border-border bg-card p-6 text-center space-y-3">
                <Lock className="w-6 h-6 mx-auto text-muted-foreground" aria-hidden="true" />
                <p className="text-xs font-medium text-muted-foreground">Not included in your trial</p>
                <h1 className="text-lg font-semibold text-foreground">{name ?? 'This part of CryptoFlow'}</h1>
                {blurb && <p className="text-sm text-muted-foreground">{blurb}</p>}
                <p className="text-sm text-muted-foreground">
                    {TRIAL_SCOPE_SENTENCE} Contact us for access{name ? ` to ${name}` : ''}.
                </p>
                <a
                    href={name ? accessRequestMailto(name) : FULL_ACCESS_MAILTO}
                    className="inline-block rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
                >
                    Contact us
                </a>
                <p className="text-xs text-muted-foreground">or write to {FULL_ACCESS_EMAIL}</p>
            </div>
        </div>
    );
}

/** Route wrapper: renders children only when the caller holds `feature`. */
export function RequireFeature({ feature, children }: { feature: FeatureKey; children: React.ReactNode }) {
    const allowed = useFeature(feature);
    if (!allowed) return <FeatureUnavailable feature={feature} />;
    return <>{children}</>;
}
